import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { customTypes, formats } from 'utils';

import { Typography } from '..';
import { isFusionNonHurricane } from 'utils/policyType';

const triggerRefRoots = (triggerRef) => {
  if (triggerRef == null) {
    return [];
  }
  return (Array.isArray(triggerRef) ? triggerRef : [triggerRef]).filter(
    Boolean,
  );
};

const getPolicyAddress = (policy) => {
  const propertyStreet = policy.propertyStreet ?? policy.property?.street;
  const propertyApt = policy.propertyApt ?? policy.property?.apt;
  const propertyCity = policy.propertyCity ?? policy.property?.city;
  const propertyState = policy.propertyState ?? policy.property?.state;
  const propertyZip = policy.propertyZip ?? policy.property?.zip;

  return `${formats.capitalize(propertyStreet)}${
    propertyApt ? `, ${propertyApt}` : ''
  }, ${formats.capitalize(propertyCity)}, ${propertyState} ${formats.zipFormat(
    propertyZip,
  )}`;
};

const SelectPolicy = ({
  id,
  policies,
  openSelectPolicy,
  onSelect,
  onClose,
  triggerRef,
  listClassName,
}) => {
  const ref = useRef();

  useEffect(() => {
    if (!openSelectPolicy || !onClose) return undefined;

    const roots = triggerRefRoots(triggerRef);

    const onPointerDown = (e) => {
      const el =
        e.target instanceof Element ? e.target : e.target?.parentElement;
      if (!el || ref.current?.contains(el)) return;
      if (roots.some((r) => r.current?.contains(el))) return;
      onClose();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [openSelectPolicy, onClose, triggerRef]);

  if (!openSelectPolicy) {
    return null;
  }

  const scrollbarClassName =
    '[scrollbar-color:#c1c1c1_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:my-[6px] [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-padding [&::-webkit-scrollbar-thumb:hover]:bg-[#a1a1a1]';

  return (
    <ul
      ref={ref}
      className={`${listClassName} absolute top-full left-0 right-0 z-[3] mt-2 w-full max-h-[min(60vh,320px)] overflow-y-auto overflow-x-hidden list-none bg-white border border-border-light rounded-[8px] p-0 shadow-[0px_4px_13px_-10px_black] m-0 ${scrollbarClassName}`}
      id={id || 'popover-select-policy'}
    >
      {policies
        .filter((policy) => !isFusionNonHurricane(policy))
        .map((policy, index) => (
          <li key={index}>
            <div
              id={`select-policy-row-${index}`}
              className="cursor-pointer hover:bg-gray-100 rounded-[4px] px-[12px] py-[8px]"
              onClick={() => onSelect(policy)}
              onKeyPress={() => onSelect(policy)}
              role="button"
              tabIndex={0}
            >
              <Typography id={`title${index}`} type="h3" component="h3">
                {getPolicyAddress(policy)}
              </Typography>
              <Typography
                id={`paragraph${index}`}
                type="h4"
                className="mt-[4px]"
              >
                {policy?.isFusion
                  ? `${policy.policyNumber} | ${policy.policyNumber}N | ${policy.policyNumber?.startsWith('H6') ? 'Slide Fusion Condo' : 'Slide Fusion'}`
                  : `${policy.policyNumber} | ${policy.type} `}
              </Typography>
            </div>
          </li>
        ))}
    </ul>
  );
};

SelectPolicy.propTypes = {
  id: PropTypes.string,
  policies: PropTypes.arrayOf(customTypes.policy),
  selectedPolicy: PropTypes.object,
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  triggerRef: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.any }),
    PropTypes.arrayOf(PropTypes.shape({ current: PropTypes.any })),
  ]),
  listClassName: PropTypes.string,
};

export default SelectPolicy;
